/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { IId } from "@octopusdeploy/octopus-server-client";
import cn from "classnames";
import * as React from "react";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { SummaryNode } from "~/components/form";
import { UnstructuredFormSection, ExpandableFormSection } from "~/components/form";
import ActionList from "../ActionList";
import styles from "./style.module.less";
interface RemovableExpandersListProps<R extends IId> {
    typeDisplayName: string;
    empty?: React.ReactNode;
    data: R[];
    listActions?: JSX.Element[];
    helpElement?: JSX.Element;
    onRow(item: R, idx: number): React.ReactNode;
    onRowSummary(item: R): SummaryNode;
    onRowHelp(item: R): string;
    onRemoveRowByIndex?(index: number): void;
}
export abstract class RemovableExpandersList<R extends IId> extends BaseComponent<RemovableExpandersListProps<R>, {}> {
    private bottomActionVisibilityThreshold = 3;
    constructor(props: RemovableExpandersListProps<R>) {
        super(props);
    }
    render() {
        const actions = (this.props.listActions || []).map((action, index) => React.cloneElement(action, { key: index }));
        return (<React.Fragment>
                <UnstructuredFormSection>
                    <div className={styles.headerBar}>
                        <div>{this.props.helpElement}</div>
                        <ActionList actions={actions}/>
                    </div>
                </UnstructuredFormSection>
                {this.props.data.length === 0 && this.props.empty && <div className={styles.emptyList}>{this.props.empty}</div>}
                {this.props.data.length !== 0 && <div>{this.props.data.map((item, index) => this.buildItem(item, index))}</div>}
                <UnstructuredFormSection>
                    <div className={cn({ [styles.hidden]: this.props.data.length < this.bottomActionVisibilityThreshold })}>
                        <ActionList actions={actions}/>
                    </div>
                </UnstructuredFormSection>
            </React.Fragment>);
    }
    private buildItem(item: R, index: number) {
        const removeAction = OverflowMenuItems.item(`Remove ${this.props.typeDisplayName}`, () => this.props.onRemoveRowByIndex!(index));
        const visualIndex = index + 1;
        return (<ExpandableFormSection key={`${visualIndex}-${item.Id}`} // An Id may not exist yet for this item, so needs to be a combination of index+Id.
         errorKey={`${visualIndex}-${item.Id}`} title={`${this.props.typeDisplayName} ${visualIndex}`} overflowMenuItems={[removeAction]} summary={this.props.onRowSummary(item)} help={this.props.onRowHelp(item)} isExpandedByDefault={!item.Id} // Expand automatically for new entries.
        >
                {this.props.onRow(item, index)}
            </ExpandableFormSection>);
    }
    static displayName = "RemovableExpandersList";
}
